import {
  CHANGED_CONDITION,
  CUG_TYPE,
  DEALS_GROUP,
  HOTEL_CARD_AREA,
  PRICE_INFO_CARD_AREA,
  PRICE_TYPE,
  REGION,
  SAVE_STATUS_TYPE,
} from './constants';
import { getStayField } from './get-stay-field';

import type { CugPrice } from '@shared/types/frontend/hotel-card';
import type { DiscountInfo } from '@shared/types/frontend/ubff/common';
import type {
  Maybe,
  Nullable,
  Optional,
  Stay,
  StaySerialized,
} from '@shared/types/utils';

export const getPriceInfoCardArea = ({
  isMapCardListHotel,
}: {
  isMapCardListHotel?: boolean;
}) => {
  if (isMapCardListHotel) {
    return PRICE_INFO_CARD_AREA.MAP_CARD_LIST;
  }

  return PRICE_INFO_CARD_AREA.UNSET_CARD_AREA;
};

export const getHotelCardArea = ({
  groupKey,
  isMapCardListHotel,
  isMapPanelHotel,
  isRecommendHotel,
  isViewedHotel,
}: {
  groupKey?: string;
  isMapPanelHotel?: boolean;
  isRecommendHotel?: boolean;
  isViewedHotel?: boolean;
  isMapCardListHotel?: boolean;
}) => {
  if (isMapCardListHotel) {
    return HOTEL_CARD_AREA.MAP_CARD_LIST;
  }
  if (isMapPanelHotel) {
    return HOTEL_CARD_AREA.MAP_PANEL;
  }
  if (isViewedHotel) {
    return HOTEL_CARD_AREA.VIEWED;
  }
  if (isRecommendHotel) {
    return HOTEL_CARD_AREA.RECOMMENDED;
  }
  if (groupKey) {
    return DEALS_GROUP[groupKey];
  }

  return HOTEL_CARD_AREA.UNSET_HOTEL_CARD_AREA;
};

export const HOTELS_FRONTEND_SCHEMA_NAME = 'hotels_frontend.HotelsAction';
export const HOTELS_FRONTEND_MINIEVENT_NAME = 'HotelsFrontendHotelsAction';

export const buildDayViewHotelCardClickedMessage = ({
  chatSessionId,
  correlationId,
  couponType,
  cug,
  currency,
  goToDVEnabled,
  greatPricePercentage,
  groupKey,
  hasCoupon,
  hotelId,
  isCoupon,
  isGoToHD,
  isMainPrice,
  isMapCardListHotel = false,
  isMapPanelHotel = false,
  isSavedHotel = false,
  isViewedHotel,
  mainPriceArea,
  partnerId,
  price,
  price_type: priceType,
  stars,
  tags,
}: {
  couponType?: string;
  cug: Nullable<CugPrice>;
  currency: string;
  goToDVEnabled: boolean;
  greatPricePercentage?: number;
  groupKey: string;
  hasCoupon?: boolean;
  hotelId: string;
  isCoupon?: boolean;
  isGoToHD?: boolean;
  isMainPrice?: boolean;
  isMapPanelHotel?: boolean;
  isMapCardListHotel?: boolean;
  isViewedHotel: boolean;
  isSavedHotel?: boolean;
  mainPriceArea: string;
  partnerId?: string;
  price?: number;
  price_type: string;
  stars: Nullable<number>;
  correlationId?: string;
  tags?: string[];
  chatSessionId?: string;
}) => {
  const cugType = cug?.type?.toUpperCase() ?? 'null';

  return {
    day_view_hotel_card_clicked: {
      is_go_to_dv: goToDVEnabled ?? false,
      has_coupon_in_search_result: hasCoupon,
      is_coupon_hotel: isCoupon,
      is_go_to_hd: isGoToHD,
      is_main_price: isMainPrice,
      main_price_area: mainPriceArea,
      partner_id: partnerId,
      price_type: priceType
        ? PRICE_TYPE[priceType.toUpperCase()]
        : PRICE_TYPE.UNSET_PRICE_TYPE,
      stars,
      coupon_type: couponType,
      is_viewed_hotel: isViewedHotel,
      hotel_card_area: getHotelCardArea({
        isRecommendHotel: false,
        isMapPanelHotel,
        isViewedHotel,
        groupKey,
        isMapCardListHotel,
      }),
      has_cug_deal: !!cug,
      cug_type: CUG_TYPE[cugType] ? CUG_TYPE[cugType] : CUG_TYPE.UNSET_CUG_TYPE,
      cug_deal_percentage: cug?.discountPercentage,
      hotel_id: parseInt(hotelId, 10),
      price: price
        ? { currency, amount: Math.round(price), unit: 'WHOLE' }
        : undefined,
      is_great_price: !!greatPricePercentage,
      great_price_percentage: greatPricePercentage,
      correlation_id: correlationId,
      is_saved_hotel: isSavedHotel,
      tags: tags?.join(','),
      chat_session_id: chatSessionId,
    },
  };
};

export const buildDayViewHotelCardClickedMessageUbff = ({
  chatSessionId,
  correlationId,
  currency,
  discountInfo,
  goToDVEnabled,
  greatPricePercentage,
  groupKey,
  hotelId,
  isGoToHD,
  isMainPrice,
  isMapCardListHotel = false,
  isMapPanelHotel = false,
  isSavedHotel = false,
  mainPriceArea,
  partnerId,
  price,
  price_type: priceType,
  stars,
  tags,
}: {
  couponType?: string;
  discountInfo?: Nullable<DiscountInfo>;
  currency: string;
  goToDVEnabled: boolean;
  greatPricePercentage?: number;
  groupKey: string;
  hotelId: string;
  isGoToHD?: boolean;
  isMainPrice?: boolean;
  isMapPanelHotel?: boolean;
  isMapCardListHotel?: boolean;
  isSavedHotel?: boolean;
  mainPriceArea: string;
  partnerId?: string;
  price?: number;
  price_type: string;
  stars: Nullable<number>;
  correlationId?: string;
  tags?: string[];
  chatSessionId?: string;
}) => {
  const cugType = discountInfo?.discountType?.toUpperCase() ?? 'null';

  return {
    day_view_hotel_card_clicked: {
      is_go_to_dv: goToDVEnabled ?? false,
      has_coupon_in_search_result: false,
      is_coupon_hotel: false,
      is_go_to_hd: isGoToHD,
      is_main_price: isMainPrice,
      main_price_area: mainPriceArea,
      partner_id: partnerId,
      price_type: priceType
        ? PRICE_TYPE[priceType.toUpperCase()]
        : PRICE_TYPE.UNSET_PRICE_TYPE,
      stars,
      hotel_card_area: getHotelCardArea({
        isRecommendHotel: false,
        isMapPanelHotel,
        isViewedHotel: false,
        groupKey,
        isMapCardListHotel,
      }),
      has_cug_deal: !!discountInfo,
      cug_type: CUG_TYPE[cugType] ? CUG_TYPE[cugType] : CUG_TYPE.UNSET_CUG_TYPE,
      cug_deal_percentage: discountInfo?.percentage,
      hotel_id: parseInt(hotelId, 10),
      price: price
        ? { currency, amount: Math.round(price), unit: 'WHOLE' }
        : undefined,
      is_great_price: !!greatPricePercentage,
      great_price_percentage: greatPricePercentage,
      correlation_id: correlationId,
      is_saved_hotel: isSavedHotel,
      tags: tags?.join(','),
      chat_session_id: chatSessionId,
    },
  };
};

export const buildAdditionalInfoPageLoadMessage = ({
  isLoggedIn,
  isSuccessful,
  pageName,
  region,
  stay,
}: {
  isLoggedIn: boolean;
  pageName: string;
  region?: string;
  stay: Optional<Stay | StaySerialized>;
  isSuccessful: boolean;
}) => ({
  page_load: {
    page_name: pageName,
    region: region ? REGION[region.toUpperCase()] : null,
    is_logged_in: isLoggedIn,
    stay: stay && getStayField(stay),
    is_successful: isSuccessful,
  },
});

export const buildAdditionalInfoSearchResultSelectedMessage = ({
  basePrice,
  chatSessionId,
  correlationId,
  currency,
  dayviewCorrelationId,
  hasCoupon,
  hotelId,
  isCugDeal,
  isGoToHD,
  isLowest,
  isMainPrice,
  isMapCardListHotel,
  isMapPanelHotel,
  isRecommendHotel,
  isUpSortHotels,
  isViewedHotel,
  mainPriceArea,
  metaFinalStatus,
  metaStatus,
  pageName,
  partnerId,
  position,
  price,
  searchId,
  tags,
  taxFees,
}: {
  hasCoupon?: boolean;
  hotelId: string;
  isCugDeal?: boolean;
  pageName: string;
  position: number;
  searchId?: Nullable<string>;
  isGoToHD?: boolean;
  isMainPrice?: boolean;
  isMapPanelHotel?: boolean;
  isMapCardListHotel?: boolean;
  isLowest?: boolean;
  isViewedHotel?: boolean;
  isRecommendHotel?: boolean;
  isUpSortHotels: boolean;
  mainPriceArea?: string;
  partnerId?: string;
  price?: number;
  correlationId?: string;
  dayviewCorrelationId?: Nullable<string>;
  basePrice?: number;
  taxFees?: number;
  currency: string;
  metaStatus?: string;
  metaFinalStatus?: string;
  tags?: string[];
  chatSessionId?: string;
}) => ({
  search_result_selected: {
    has_cug_deal: isCugDeal,
    has_coupon: hasCoupon,
    is_up_sorted_hotels: isUpSortHotels,
    hotel_id: parseInt(hotelId, 10),
    is_go_to_hd: isGoToHD,
    is_main_price: isMainPrice,
    is_lowest: isLowest,
    is_recommend_hotel: isRecommendHotel,
    hotel_card_area: getHotelCardArea({
      isRecommendHotel,
      isMapPanelHotel,
      isViewedHotel,
      isMapCardListHotel,
    }),
    is_viewed_hotel: isViewedHotel,
    main_price_area: mainPriceArea,
    partner_id: partnerId,
    position,
    price: price
      ? { currency, amount: Math.round(price), unit: 'WHOLE' }
      : undefined,
    search_id: searchId,
    page_name: pageName,
    correlation_id: correlationId,
    day_view_correlation_id: dayviewCorrelationId,
    base_price: basePrice
      ? {
          currency,
          amount: Math.floor(basePrice),
          unit: 'WHOLE',
        }
      : undefined,
    tax_fees: taxFees
      ? { currency, amount: Math.floor(taxFees), unit: 'WHOLE' }
      : undefined,
    search_status: metaStatus,
    final_status: metaFinalStatus,
    tags: tags?.join(','),
    chat_session_id: chatSessionId,
  },
});

export const buildAdditionalInfoViewButtonClicked = (
  clickArea: string,
  correlationId?: string,
) => ({
  view_button_clicked: {
    click_area: clickArea,
    correlation_id: correlationId,
  },
});

export const buildAdditionalInfoSearchConditionChanged = ({
  adultChanged,
  changedCondition,
  childrenAgesChanged,
  hotelId,
  isSearchWithChildren,
  roomNumChanged,
  rooms,
}: {
  adultChanged: Maybe<boolean>;
  changedCondition: string;
  childrenAgesChanged: Maybe<boolean>;
  hotelId?: string;
  isSearchWithChildren: boolean;
  roomNumChanged: Maybe<boolean>;
  rooms: number;
}) => ({
  search_condition_changed: {
    hotel_id: hotelId && parseInt(hotelId, 10),
    changed_condition: changedCondition,
    rooms,
    is_search_with_children: isSearchWithChildren,
    guest_rooms_changed_info:
      changedCondition === CHANGED_CONDITION.GUEST_OR_ROOMS
        ? {
            has_number_of_adults_changed: adultChanged,
            has_number_of_children_changed: childrenAgesChanged,
            has_number_of_rooms_changed: roomNumChanged,
          }
        : undefined,
  },
});

export const buildAdditionalInfoSaveStatusChanged = ({
  actionId,
  clickArea,
  correlationId,
  hotelId,
  isLoggedIn,
  saveStatus,
  stay,
}: {
  clickArea: string;
  hotelId: string;
  correlationId?: string;
  isLoggedIn: boolean;
  stay: Stay;
  saveStatus: string;
  actionId: string;
}) => ({
  save_status_changed: {
    click_area: clickArea,
    hotel_id: hotelId,
    correlation_id: correlationId,
    is_logged_in: isLoggedIn,
    stay: getStayField(stay),
    action_id: actionId,
    save_status:
      SAVE_STATUS_TYPE[saveStatus] || SAVE_STATUS_TYPE.UNSET_SAVE_HOTEL_STATUS,
  },
});

export const buildAdditionalInfoSaveNotificationClicked = ({
  clickArea,
  hotelId,
}: {
  clickArea: string;
  hotelId: string;
}) => ({
  save_notification_clicked: {
    click_area: clickArea,
    hotel_id: hotelId,
  },
});

export const buildAdditionalInfoPriceBreakdownSelected = ({
  priceType,
}: {
  priceType: string;
}) => ({
  price_breakdown_selected: {
    price_type: priceType,
  },
});
